import React, { useEffect, useRef, useState } from 'react';
import { Link, NavLink, useLocation, useNavigate } from 'react-router-dom';
import { AnimatePresence, m } from 'framer-motion';
import { ChevronDown, Inbox, Menu, X, User, LogOut } from 'lucide-react';
import AppearanceSwitch from '../../theme/AppearanceSwitch';
import logo from '../../assets/logo-sm.png';
import { useAuth } from '../../context/AuthContext';
import { Button, cx } from '../ui';
{/*{ to: '/invest', label: 'Invest', desc: 'Partner with us on SCOUT' }*/}
{/*{ to: '/analyser/SCOUT', label: 'SCOUT' },*/}

export const NAV = [
  {
    label: 'Spectral library',
    items: [
      { to: '/spectral-library', label: 'Open the library', desc: 'Explore reference spectra and analyse your own' },
      { to: '/software/desktop-application', label: 'Desktop app', desc: 'Batch processing on your own machine' },
      { to: '/documentation', label: 'Documentation', desc: 'Guides, file formats and methods' },
      { to: '/research-impact', label: 'Research & impact', desc: 'Publications that use the platform' },
    ],
  },  
  { to: '/pricing', label: 'Pricing' },
  {
    label: 'Company',
    items: [
      { to: '/about', label: 'About us', desc: 'The team and why we exist' },
      { to: '/news', label: 'News', desc: 'Updates and articles' },
      { to: '/contact-us', label: 'Contact', desc: 'Talk to a person' },
    ],
  },
  {
    label: 'Legal',
    items: [
      { to: '/privacy-policy', label: 'Our privacy policy', desc: 'How the site handles personal data' },
      { to: '/terms', label: 'Terms', desc: 'Our terms of service' },
      { to: '/cookies', label: 'Cookie usage', desc: 'Our cookie usage across the site' },
      { to: '/legal-notice', label: 'Legal notice', desc: 'Legal notice for Plastic Analytics' },
    ],
  },
];

function Dropdown({ group }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  const location = useLocation();
  useEffect(() => setOpen(false), [location.pathname]);
  useEffect(() => {
    if (!open) return undefined;
    const onDown = (e) => { if (!ref.current?.contains(e.target)) setOpen(false); };
    const onKey = (e) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('pointerdown', onDown);
    document.addEventListener('keydown', onKey);
    return () => { document.removeEventListener('pointerdown', onDown); document.removeEventListener('keydown', onKey); };
  }, [open]);
  const active = group.items.some((i) => location.pathname.startsWith(i.to));
  return (
    <div ref={ref} className="relative" onMouseLeave={() => setOpen(false)}>
      <button type="button" aria-expanded={open} onClick={() => setOpen((o) => !o)} onMouseEnter={() => setOpen(true)}
        className={cx('inline-flex items-center gap-1 rounded-pill px-3.5 py-2 text-sm font-medium transition-colors hover:text-ink',
          active ? 'text-ink' : 'text-ink-muted')}>
        {group.label}
        <ChevronDown className={cx('h-3.5 w-3.5 transition-transform duration-200', open && 'rotate-180')} />
      </button>
      <AnimatePresence>
        {open && (
          <m.div initial={{ opacity: 0, y: -6, scale: 0.98 }} animate={{ opacity: 1, y: 0, scale: 1 }} exit={{ opacity: 0, y: -6, scale: 0.98 }}
            transition={{ type: 'spring', bounce: 0, duration: 0.25 }} style={{ transformOrigin: 'top left' }}
            className="absolute left-0 top-full z-50 pt-2">
            <div className="w-80 rounded-3xl bg-surface p-2 shadow-float ring-1 ring-line">
              {group.items.map((i) => (
                <Link key={i.to} to={i.to} className="block rounded-2xl px-4 py-3 transition hover:bg-paper-sunk">
                  <span className="block text-sm font-medium text-ink">{i.label}</span>
                  <span className="mt-0.5 block text-xs text-ink-muted">{i.desc}</span>
                </Link>
              ))}
            </div>
          </m.div>
        )}
      </AnimatePresence>
    </div>
  );
}

// Live count of unread chat messages, for team accounts only.
function useAdminUnread(isAdmin) {
  const [n, setN] = useState(0);
  useEffect(() => {
    if (!isAdmin) return undefined;
    let unsub = () => {};
    let live = true;
    Promise.all([import('firebase/firestore'), import('../../lib/db')]).then(([{ collection, onSnapshot, query, where }, { db }]) => {
      if (!live) return;
      unsub = onSnapshot(query(collection(db, 'support_threads'), where('unread_admin', '>', 0)),
        (s) => setN(s.docs.reduce((t, d) => t + (d.data().unread_admin || 0), 0)), () => {});
    });
    return () => { live = false; unsub(); };
  }, [isAdmin]);
  return n;
}

function AccountMenu() {
  const { user, signOut, isAdmin } = useAuth();
  const unread = useAdminUnread(isAdmin);
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  useEffect(() => {
    if (!open) return undefined;
    const onDown = (e) => { if (!ref.current?.contains(e.target)) setOpen(false); };
    document.addEventListener('pointerdown', onDown);
    return () => document.removeEventListener('pointerdown', onDown);
  }, [open]);
  const initial = (user?.displayName || user?.email || '?').trim().charAt(0).toUpperCase();
  return (
    <div ref={ref} className="relative">
      <button type="button" onClick={() => setOpen((o) => !o)} aria-expanded={open} aria-label="Account menu"
        className="relative flex h-9 w-9 items-center justify-center rounded-full bg-primary text-sm font-semibold text-on-primary transition active:scale-95">
        {initial}
        {unread > 0 && <span className="absolute -right-1 -top-1 flex h-4 min-w-[1rem] items-center justify-center rounded-full bg-signal-bad px-1 text-[10px] font-semibold text-white ring-2 ring-paper" aria-label={`${unread} unread messages`}>{unread}</span>}
      </button>
      <AnimatePresence>
        {open && (
          <m.div initial={{ opacity: 0, y: -6 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -6 }} transition={{ duration: 0.15 }}
            className="absolute right-0 top-full z-50 mt-2 w-64 rounded-3xl bg-surface p-2 shadow-float ring-1 ring-line" style={{ transformOrigin: 'top right' }}>
            <p className="truncate px-4 pb-2 pt-3 text-xs text-ink-muted">{user?.email}</p>
            {isAdmin && (
              <Link to="/admin/messages" onClick={() => setOpen(false)} className="flex items-center gap-2 rounded-2xl px-4 py-2.5 text-sm hover:bg-paper-sunk">
                <Inbox className="h-4 w-4" />Messages{unread > 0 && <span className="ml-auto rounded-full bg-signal-bad px-2 py-0.5 text-[11px] font-semibold text-white">{unread}</span>}
              </Link>
            )}
            <Link to="/account" onClick={() => setOpen(false)} className="flex items-center gap-2 rounded-2xl px-4 py-2.5 text-sm hover:bg-paper-sunk"><User className="h-4 w-4" />Account & billing</Link>
            <button type="button" onClick={async () => { setOpen(false); await signOut(); navigate('/'); }}
              className="flex w-full items-center gap-2 rounded-2xl px-4 py-2.5 text-left text-sm hover:bg-paper-sunk"><LogOut className="h-4 w-4" />Sign out</button>
          </m.div>
        )}
      </AnimatePresence>
    </div>
  );
}

export default function Header() {
  const { isMember, isAdmin } = useAuth();
  const location = useLocation();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [hidden, setHidden] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const lastY = useRef(0);

  useEffect(() => setMobileOpen(false), [location.pathname]);

  useEffect(() => {
    const onScroll = () => {
      const y = window.scrollY;
      setScrolled(y > 8);
      setHidden(y > 160 && y > lastY.current + 4);
      if (y < lastY.current - 4 || y < 160) setHidden(false);
      lastY.current = y;
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => {
    document.body.style.overflow = mobileOpen ? 'hidden' : '';
    return () => { document.body.style.overflow = ''; };
  }, [mobileOpen]);

  return (
    <>
      <a href="#main" className="sr-only focus:not-sr-only focus:fixed focus:left-4 focus:top-4 focus:z-[200] focus:rounded-pill focus:bg-primary focus:px-4 focus:py-2 focus:text-on-primary">Skip to content</a>
      <m.header animate={{ y: hidden && !mobileOpen ? -96 : 0 }} transition={{ type: 'spring', bounce: 0, duration: 0.35 }}
        className="fixed inset-x-0 top-0 z-50 px-3 pt-3 sm:px-4 sm:pt-4">
        <nav aria-label="Main" className={cx('glass mx-auto flex h-14 max-w-content items-center justify-between rounded-pill pl-3 pr-2 ring-1 transition-shadow sm:pl-4',
          scrolled ? 'shadow-float ring-line' : 'ring-line/60')}>
          <Link to="/" className="flex items-center gap-2 rounded-pill pr-2" aria-label="Plastic Analytics home">
            <span className="rounded-xl bg-white p-0.5 ring-1 ring-line"><img src={logo} alt="" className="h-6 w-auto" width="39" height="24" decoding="async" /></span>
            <span className="text-[0.95rem] font-semibold tracking-tight text-ink">Plastic Analytics</span>
          </Link>

          <div className="hidden items-center lg:flex">
            {NAV.map((item) => (item.items ? <Dropdown key={item.label} group={item} /> : (
              <NavLink key={item.to} to={item.to}
                className={({ isActive }) => cx('rounded-pill px-3.5 py-2 text-sm font-medium transition-colors hover:text-ink', isActive ? 'text-ink' : 'text-ink-muted')}>
                {item.label}
              </NavLink>
            )))}
          </div>

          <div className="flex items-center gap-1.5">
            {isMember ? <AccountMenu /> : (
              <>
                <Button to="/sign-in" variant="ghost" size="sm" className="hidden sm:inline-flex">Sign in</Button>
                <Button to="/register?plan=free" size="sm" className="hidden sm:inline-flex">Start free trial</Button>
              </>
            )}
            <button type="button" className="inline-flex h-10 w-10 items-center justify-center rounded-full text-ink transition hover:bg-ink/5 active:scale-95 lg:hidden"
              aria-label={mobileOpen ? 'Close menu' : 'Open menu'} aria-expanded={mobileOpen} onClick={() => setMobileOpen((o) => !o)}>
              {mobileOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
            </button>
          </div>
        </nav>
      </m.header>

      <AnimatePresence>
        {mobileOpen && (
          <m.div className="fixed inset-0 z-40 overflow-y-auto bg-paper px-6 pb-10 pt-24 lg:hidden"
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} transition={{ duration: 0.2 }}>
            <div className="mx-auto max-w-md space-y-8">
              {NAV.map((item) => (item.items ? (
                <div key={item.label}>
                  <p className="eyebrow">{item.label}</p>
                  <ul className="mt-3 space-y-1">
                    {item.items.map((i) => (
                      <li key={i.to}><Link to={i.to} className="block rounded-2xl py-2 text-2xl font-semibold tracking-tight text-ink">{i.label}</Link></li>
                    ))}
                  </ul>
                </div>
              ) : (
                <Link key={item.to} to={item.to} className="block text-2xl font-semibold tracking-tight text-ink">{item.label}</Link>
              )))}
              {!isMember && (
                <div className="grid gap-3 pt-4">
                  <Button to="/register?plan=free" size="lg" full>Start free trial</Button>
                  <Button to="/sign-in" variant="secondary" size="lg" full>Sign in</Button>
                </div>
              )}
              {isMember && <Button to="/account" variant="secondary" size="lg" full>Account & billing</Button>}
              {isAdmin && <Button to="/admin/messages" variant="secondary" size="lg" full><Inbox className="h-5 w-5" />Messages</Button>}
              <div className="flex items-center justify-between border-t border-line pt-6">
                <span className="text-sm text-ink-muted">Appearance</span>
                <AppearanceSwitch />
              </div>
            </div>
          </m.div>
        )}
      </AnimatePresence>
    </>
  );
}
